import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/atoms/Button";
import { Logo } from "@/components/atoms/Logo";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Error Boundary global — captura qualquer erro não tratado durante a
 * renderização da árvore React e mostra uma tela explicativa em vez de
 * deixar a página em branco. Sem isso, um erro de runtime (ex.: Firebase
 * mal configurado, variável indefinida) resulta em tela branca silenciosa,
 * com o erro visível apenas no console do navegador (F12).
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error("[Palpiteiro] Erro não tratado:", error, info.componentStack);
  }

  render() {
    if (this.state.error) {
      return (
        <div className="flex min-h-dvh flex-col items-center justify-center gap-4 bg-background px-6 text-center">
          <Logo variant="mark" />
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-destructive/10 text-destructive">
            <AlertTriangle className="h-6 w-6" />
          </div>
          <div className="max-w-sm space-y-1">
            <h1 className="font-display text-lg font-bold">A aplicação encontrou um erro</h1>
            <p className="text-sm text-muted-foreground">
              Abra o console do navegador (F12 → aba "Console") para ver o detalhe técnico abaixo e copiá-lo.
            </p>
          </div>
          <pre className="max-h-40 w-full max-w-md overflow-auto rounded-xl bg-muted p-3 text-left text-xs text-muted-foreground">
            {this.state.error.message}
          </pre>
          <Button onClick={() => window.location.reload()}>
            <RefreshCw className="h-4 w-4" /> Recarregar
          </Button>
        </div>
      );
    }

    return this.props.children;
  }
}
